import { ArrowDownRight, ArrowRight, ArrowUpRight, ChevronRight, Clock } from 'lucide-react';
import type { ReactNode } from 'react';
import { cn } from '../lib/cn.ts';
import { cardSurface } from '../primitives/card.tsx';
import { formatRelative } from '../lib/format.ts';
import { deltaDirection, deltaMeaning, formatDelta, formatMetric } from '../lib/metric.ts';
import { useNow } from '../lib/useNow.ts';
import { Skeleton } from './Skeleton.tsx';
import { Sparkline } from './Sparkline.tsx';
import { Tooltip } from './Tooltip.tsx';

/** Data older than this gets a muted stale warning (DesignSystem 4.2). Mirrors the metric.stale-after token. */
export const METRIC_STALE_MS = 120_000;

export interface MetricDelta {
  /** The change: a fraction for percent (0.12 is +12%), else an absolute amount. */
  value: number;
  kind?: 'percent' | 'absolute' | 'money';
  /** Which way is good for this metric. Cost up is bad; success rate up is good. */
  goodWhen?: 'up' | 'down' | 'neither';
  /** The comparison period: "vs yesterday". */
  period?: string;
}

export interface MetricTileProps {
  /** Sentence case, no colon: "Sessions running". */
  label: string;
  /** null renders the empty state. */
  value: number | string | null;
  /** Always shown beside the value: "sessions", "tokens", "%". Put currency inside the value ("$4.20"). */
  unit?: string;
  /** Formats a numeric value. Default: at most 4 significant characters (12.7k). */
  format?: (n: number) => string;
  delta?: MetricDelta;
  /** Same time range as the page, oldest first. */
  trend?: number[];
  /** What the trend covers, for screen readers: "last 24 hours". */
  trendLabel?: string;
  /** The tile opens the list filtered to exactly this number. */
  href?: string;
  onOpen?: () => void;
  /** When the value was computed; stale after 2 minutes. */
  updatedAt?: number;
  state?: 'loading' | 'error' | 'full';
  /** Shown in the error state: what to do next. */
  errorText?: string;
  /** Shown when value is null. */
  emptyText?: string;
  icon?: ReactNode;
  className?: string;
}

const DELTA_ICON = { up: ArrowUpRight, down: ArrowDownRight, flat: ArrowRight };
const DELTA_CLASS = { good: 'text-status-success', bad: 'text-status-danger', neutral: 'text-fg-subtle' };

function Delta({ delta }: { delta: MetricDelta }) {
  const dir = deltaDirection(delta.value);
  const meaning = deltaMeaning(delta.value, delta.goodWhen);
  const Icon = DELTA_ICON[dir];
  const text = formatDelta(delta.value, delta.kind);
  const words = dir === 'flat' ? 'no change' : `${dir} ${text.replace(/^[+-]/, '')}`;
  return (
    <span className={cn('inline-flex items-center gap-0.5 text-caption font-medium num', DELTA_CLASS[meaning])}>
      <Icon className="size-3.5 shrink-0" aria-hidden />
      <span aria-hidden>{text}</span>
      <span className="sr-only">
        {words}
        {meaning !== 'neutral' ? `, ${meaning === 'good' ? 'better' : 'worse'}` : ''}
      </span>
      {delta.period && <span className="ml-1 font-normal text-fg-subtle">{delta.period}</span>}
    </span>
  );
}

/** The label, with "Updated 12 s ago" on hover; stale data adds a muted clock. */
function Label({ label, updatedAt }: { label: string; updatedAt?: number }) {
  const now = useNow(updatedAt == null ? 60_000 : 15_000);
  if (updatedAt == null) return <span className="truncate">{label}</span>;
  const stale = now - updatedAt > METRIC_STALE_MS;
  const text = `Updated ${formatRelative(updatedAt, now)}`;
  return (
    <>
      <Tooltip content={stale ? `${text}. This number may be out of date.` : text} delay={400}>
        <span className="truncate">{label}</span>
      </Tooltip>
      {stale ? (
        <span role="img" aria-label={`Out of date: ${text}`} className="inline-flex shrink-0 text-fg-subtle">
          <Clock className="size-3.5" aria-hidden />
        </span>
      ) : (
        <span className="sr-only">{text}</span>
      )}
    </>
  );
}

/** One KPI: value with its unit, change against the previous period, a sparkline, and a link to the filtered list. */
export function MetricTile({
  label,
  value,
  unit,
  format = formatMetric,
  delta,
  trend,
  trendLabel,
  href,
  onOpen,
  updatedAt,
  state = 'full',
  errorText = 'Could not load this number. It retries on its own.',
  emptyText = 'No data yet',
  icon,
  className,
}: MetricTileProps) {
  const interactive = state === 'full' && (href != null || onOpen != null);
  const shown = typeof value === 'number' ? format(value) : value;

  const body = (
    <>
      <div className="flex min-w-0 items-center gap-1.5 text-caption text-fg-muted">
        {icon && <span className="text-fg-subtle [&_svg]:size-3.5" aria-hidden>{icon}</span>}
        <Label label={label} updatedAt={state === 'full' ? updatedAt : undefined} />
        {interactive && <ChevronRight className="ml-auto size-3.5 shrink-0 text-fg-faint transition-colors group-hover/tile:text-fg-muted" aria-hidden />}
      </div>

      {state === 'loading' ? (
        <div className="mt-2 flex items-end justify-between gap-3" role="status" aria-label={`Loading ${label}`}>
          <Skeleton className="h-7 w-20" />
          <Skeleton className="h-(--metric-sparkline-height) w-24" />
        </div>
      ) : state === 'error' ? (
        <p className="mt-2 text-ui text-fg-muted">
          <span className="font-medium text-status-danger">Unavailable. </span>
          {errorText}
        </p>
      ) : shown == null ? (
        <p className="mt-2 text-ui text-fg-subtle">{emptyText}</p>
      ) : (
        <div className="mt-1.5 flex items-end justify-between gap-3">
          <div className="flex min-w-0 flex-col gap-1">
            <p className="flex min-w-0 items-baseline gap-1.5">
              <span className="truncate font-display text-metric text-fg num-display">{shown}</span>
              {unit && <span className="shrink-0 text-ui text-fg-subtle">{unit}</span>}
            </p>
            {delta && <Delta delta={delta} />}
          </div>
          {trend && trend.length > 1 && (
            <Sparkline
              values={trend}
              tone="neutral"
              width={96}
              height={28}
              label={`${label}${trendLabel ? `, ${trendLabel}` : ''}: now ${shown}${unit ? ` ${unit}` : ''}`}
              className="mb-0.5"
            />
          )}
        </div>
      )}
    </>
  );

  const frame = cn(
    cardSurface,
    'group/tile flex min-w-0 flex-col p-(--metric-padding) text-left',
    interactive && 'transition-colors duration-(--duration-fast) hover:border-border-strong hover:bg-raised',
    className,
  );

  if (interactive && href != null) {
    return (
      <a href={href} className={cn(frame, 'no-underline')} onClick={onOpen ? (e) => (e.preventDefault(), onOpen()) : undefined}>
        {body}
      </a>
    );
  }
  if (interactive) {
    return (
      <button type="button" className={cn(frame, 'w-full')} onClick={onOpen}>
        {body}
      </button>
    );
  }
  return <div className={frame}>{body}</div>;
}
